/**
 * useWebPush — Web Push subscription lifecycle.
 *
 * Handles feature detection, VAPID key fetch, permission request, and
 * server-side subscription registration. Consumers get a compact
 * `{supported, permission, subscribed, enable, disable, busy}` API.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// urlBase64ToUint8Array — VAPID public key needs to be a Uint8Array for
// PushSubscription.applicationServerKey.
const b64ToU8 = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

export const useWebPush = (token) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch { /* noop */ }
    })();
  }, []);

  const enable = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setBusy(true);
    try {
      // Fetch VAPID key
      const keyRes = await axios.get(`${API}/api/notifications/vapid-public-key`);
      const vapidPub = keyRes.data?.public_key;
      if (!vapidPub) return { ok: false, reason: 'server_not_configured' };

      // Prompt permission (only from a real user gesture)
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return { ok: false, reason: perm };

      // Subscribe
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToU8(vapidPub),
      });
      const json = sub.toJSON();
      await axios.post(`${API}/api/notifications/subscribe`, {
        endpoint: json.endpoint,
        keys: json.keys,
      }, { headers });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'error', error: e };
    } finally { setBusy(false); }
  }, [supported, token]);

  const disable = useCallback(async () => {
    if (!supported) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await axios.post(`${API}/api/notifications/unsubscribe`,
            { endpoint: sub.endpoint }, { headers });
        } catch { /* server row cleanup best-effort */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    } finally { setBusy(false); }
  }, [supported, token]);

  return { supported, permission, subscribed, enable, disable, busy };
};
